/**
 * Compte — destination unique à onglets de niveau 2 (`coquille.md` §4 : `TABS`, `TAB_ACTIVE`/
 * `TAB_IDLE`, liseré haut, état dans l'URL en paramètre de requête). Fusionne ce qu'étaient
 * `/profil` et `/preferences`, plus les clés API et l'aide MCP qui vivaient enfouies dans
 * Préférences (§ audit exiUI 2026-09-27) : quatre sous-thèmes du même sujet — identité,
 * réglages, sécurité agent, aide — au lieu de deux routes disjointes et un composant enfoui.
 *
 * `/profil` et `/preferences` redirigent ici (`App.jsx`).
 */

import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { PageBody, PageHeader, PrimaryButton } from '../components/common.jsx';
import { ApiKeysPanel } from '../components/ApiKeysPanel.jsx';
import { McpHelpPanel } from '../components/McpHelpPanel.jsx';
import { ProfilePage } from './ProfilePage.jsx';
import { PreferencesPage } from './PreferencesPage.jsx';
import { TAB_ACTIVE, TAB_IDLE } from '../ui/theme.js';

const TABS = ['profil', 'preferences', 'cles-api', 'aide-mcp'];

/** Littéraux dans l'objet — le gate `keys.test.js` ne voit pas une clé de gabarit calculée. */
const TAB_LABEL_KEYS = {
  profil: 'account.tabs.profile',
  preferences: 'account.tabs.preferences',
  'cles-api': 'account.tabs.apiKeys',
  'aide-mcp': 'account.tabs.mcpHelp',
};

export function AccountPage() {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [creatingKey, setCreatingKey] = useState(false);

  const activeTab = TABS.includes(searchParams.get('onglet'))
    ? searchParams.get('onglet')
    : 'profil';
  const setActiveTab = (tab) => {
    const next = new URLSearchParams(searchParams);
    next.set('onglet', tab);
    setSearchParams(next, { replace: true });
  };

  // LE primaire de l'écran (règle de cohérence 1.3.0 d'`@exidis/ui`) : « Nouvelle clé » sur
  // Clés API seulement. Profil, Préférences et Aide MCP n'ont pas d'action principale.
  return (
    <div>
      <PageHeader
        eyebrow={t('account.eyebrow')}
        title={t('account.title')}
        subtitle={t('account.subtitle')}
        primary={
          activeTab === 'cles-api' && (
            <PrimaryButton onClick={() => setCreatingKey(true)}>
              {t('apiKeys.newKey')}
            </PrimaryButton>
          )
        }
      />

      <div className="border-b-2 border-line">
        <nav
          className="mx-auto flex w-full max-w-(--largeur-corps) items-end gap-1 px-4 pt-2"
          aria-label={t('account.tabsAriaLabel')}
        >
          {TABS.map((tab) => (
            <button
              key={tab}
              type="button"
              className={activeTab === tab ? TAB_ACTIVE : TAB_IDLE}
              aria-current={activeTab === tab ? 'page' : undefined}
              onClick={() => setActiveTab(tab)}
            >
              {t(TAB_LABEL_KEYS[tab])}
            </button>
          ))}
        </nav>
      </div>

      <PageBody className="p-4">
        {activeTab === 'profil' && (
          <div className="flex flex-col gap-4">
            <ProfilePage />
          </div>
        )}
        {activeTab === 'preferences' && <PreferencesPage />}
        {activeTab === 'cles-api' && (
          <ApiKeysPanel creating={creatingKey} onCreatingChange={setCreatingKey} />
        )}
        {activeTab === 'aide-mcp' && <McpHelpPanel />}
      </PageBody>
    </div>
  );
}
